import React, {
  ReactNode,
  SyntheticEvent,
  useState,
  useEffect,
  memo,
  useMemo,
  useCallback,
} from "react";
import {
  Autocomplete,
  MenuItem,
  MenuItemProps,
  Stack,
  Typography,
  typographyClasses,
} from "@mui/material";
import { CircularProgress } from "@mui/material";
import { capitalize, debounce } from "@mui/material/utils";
import { MakeModelAutocompleteOption } from "types";
import { TextValidator } from "react-material-ui-form-validator";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { makeModelAutocomplete } from "api/vehicles";
import { TOAST_TYPE } from "context/toastContext";

export interface MakeModelAutocompleteProps {
  make: string | null;
  model: string | null;
  setMake: (make: string | null) => void;
  setModel: (model: string | null) => void;
  readonly?: boolean;
  required?: boolean;
  disabled?: boolean;
  variant?: "standard" | "outlined" | "filled";
  margin?: "none" | "dense" | "normal";
}

const MakeModelAutocomplete = ({
  make,
  model,
  setMake,
  setModel,
  readonly = false,
  disabled = false,
  required = false,
  variant = "outlined",
  margin = "none",
}: MakeModelAutocompleteProps): ReactNode => {
  const { t } = useTranslation();
  const [makeModelOptions, setMakeModelOptions] = useState<
    MakeModelAutocompleteOption[]
  >([]);
  const [makeOpen, setMakeOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [makeInputValue, setMakeInputValue] = useState("");
  const [modelInputValue, setModelInputValue] = useState("");
  const [loading, setLoading] = useState(false);

  const makeOptions = useMemo(() => {
    const options: string[] = [];
    if (makeInputValue === "" || !makeOpen || makeModelOptions.length === 0) {
      return options;
    }

    const set = new Set(
      makeModelOptions.map((o: MakeModelAutocompleteOption) => o.make),
    );
    options.push(...Array.from(set));

    return options;
  }, [makeModelOptions]);

  const modelOptions = useMemo(() => {
    const options: string[] = [];
    if (modelInputValue === "" || !modelOpen || makeModelOptions.length === 0) {
      return options;
    }
    const set = new Set(
      makeModelOptions
        .filter(
          (o: MakeModelAutocompleteOption) =>
            o.make === makeInputValue || !makeInputValue,
        )
        .map((o: MakeModelAutocompleteOption) => o.model),
    );
    options.push(...Array.from(set));
    return options;
  }, [makeModelOptions]);

  useEffect(() => {
    debounce(async () => {
      setLoading(true);
      const res = await makeModelAutocomplete(makeInputValue, modelInputValue);
      if (res.ok) {
        const data = await res.json();
        setMakeModelOptions(data);
      }
      setLoading(false);
    }, 300)();
  }, [makeInputValue, modelInputValue]);

  const handleOnMakeChange = (e: SyntheticEvent, v: string | null) => {
    // this is a clear event
    if (v === null) {
      setMakeModelOptions([]);
      setModelInputValue("");
      setModel(null);
      setMakeOpen(false);
      setModelOpen(false);
    }

    setMakeInputValue(v || "");
    setMake(v);
  };
  const handleOnMakeInputChange = (e: SyntheticEvent, value: string) => {
    setMakeInputValue(value);
  };
  const handleOnModelChange = (e: SyntheticEvent, v: string | null) => {
    // this is a clear event
    if (v === null) {
      setModelInputValue("");
      setModel(null);
      return;
    }

    setModel(v);
    setModelInputValue(v);

    const option = makeModelOptions.find(
      (option: MakeModelAutocompleteOption) =>
        option.model.toLowerCase() === v.toLowerCase(),
    );
    if (option && !make) {
      setMake(option.make);
      setMakeInputValue(option.make);
      setModelOpen(false);
      setMakeOpen(false);
    }
  };
  const handleOnModelInputChange = (e: SyntheticEvent, value: string) => {
    setModelInputValue(value);
  };
  const getOptionLabel = (option: string | MakeModelAutocompleteOption) => {
    if (typeof option === "string") {
      return option;
    }
    return "";
  };
  const handleMakeOpen = () => {
    setMakeOpen(true);
  };
  const handleMakeClose = () => {
    setMakeOpen(false);
  };
  const handleModelOpen = () => {
    setModelOpen(true);
  };
  const handleModelClose = () => {
    setModelOpen(false);
  };
  const renderOption = (props: MenuItemProps, option: string) => {
    // eslint-disable-next-line react/prop-types
    const { key, ...optionProps } = props;
    return (
      <MenuItem key={key as string} value={option} divider {...optionProps}>
        <Typography variant={"body2"} sx={{ p: 1 }}>
          {option}
        </Typography>
      </MenuItem>
    );
  };

  return (
    <Stack
      direction={"row"}
      gap={2}
      alignItems={"center"}
      justifyContent={"space-between"}
    >
      <Autocomplete
        disabled={disabled}
        id="make-autocomplete"
        disablePortal={false}
        blurOnSelect
        clearOnBlur
        clearOnEscape
        open={makeOpen}
        onOpen={handleMakeOpen}
        onClose={handleMakeClose}
        filterSelectedOptions
        filterOptions={(x) => x}
        getOptionLabel={getOptionLabel}
        getOptionKey={(option) => option || ""}
        options={makeOptions}
        renderOption={renderOption}
        value={make}
        fullWidth
        sx={{ flex: 1 }}
        noOptionsText={
          !make && (
            <MenuItem key={"make-no-options"} value={"make-no-options"}>
              <Typography variant={"body2"} textTransform={"capitalize"}>
                {t("enterTextToSearch")}
              </Typography>
            </MenuItem>
          )
        }
        loading={loading && makeOpen}
        loadingText={
          <MenuItem key={"make-loading"} value={"make-loading"}>
            <Typography variant={"body2"} textTransform={"capitalize"}>
              {t("searching")}
            </Typography>
          </MenuItem>
        }
        onInputChange={handleOnMakeInputChange}
        onChange={handleOnMakeChange}
        renderInput={(params) => (
          <TextValidator
            {...params}
            label={`${capitalize(t("make"))}${required ? " *" : ""}`}
            value={make}
            margin={margin}
            variant={variant}
            name={"make"}
            sx={{ flex: 1 }}
            validators={required ? ["required"] : []}
            errorMessages={[t("invalid.required", { name: t("make") })]}
            fullWidth
            InputProps={{
              ...params.InputProps,
              sx: {
                textTransform: "capitalize",
                [`.${typographyClasses.root}`]: {
                  textTransform: "capitalize",
                  variant: "body1",
                },
              },
              endAdornment: (
                <React.Fragment>
                  {loading && makeOpen ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
            inputProps={{
              ...params.inputProps,
              style: {
                cursor: readonly ? "default" : "text",
              },
            }}
          />
        )}
      />
      <Autocomplete
        id="model-autocomplete"
        blurOnSelect
        disabled={disabled}
        open={modelOpen}
        sx={{ flex: 1 }}
        onOpen={handleModelOpen}
        onClose={handleModelClose}
        filterSelectedOptions
        filterOptions={(x) => x}
        options={modelOptions}
        getOptionLabel={getOptionLabel}
        getOptionKey={(option) => option || ""}
        value={model}
        fullWidth
        noOptionsText={
          !model && (
            <MenuItem key={"model-no-options"} value={"model-no-options"}>
              <Typography variant={"body2"} textTransform={"capitalize"}>
                {t("enterTextToSearch")}
              </Typography>
            </MenuItem>
          )
        }
        loading={loading && modelOpen}
        loadingText={
          <MenuItem key={"model-loading"} value={"model-loading"}>
            <Typography variant={"body2"} textTransform={"capitalize"}>
              {t("searching")}
            </Typography>
          </MenuItem>
        }
        renderOption={renderOption}
        onInputChange={handleOnModelInputChange}
        onChange={handleOnModelChange}
        renderInput={(params) => (
          <TextValidator
            {...params}
            label={`${capitalize(t("model"))}${required ? " *" : ""}`}
            value={model}
            name={"model"}
            margin={margin}
            variant={variant}
            validators={required ? ["required"] : []}
            errorMessages={[t("invalid.required", { name: t("model") })]}
            fullWidth
            sx={{ flex: 1 }}
            InputProps={{
              ...params.InputProps,
              sx: {
                [`.${typographyClasses.root}`]: {
                  variant: "body1",
                },
              },
              endAdornment: (
                <React.Fragment>
                  {loading && modelOpen ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
              readOnly: readonly,
            }}
            inputProps={{
              ...params.inputProps,
              style: {
                cursor: readonly ? "default" : "text",
              },
            }}
          />
        )}
      />
    </Stack>
  );
};

export default MakeModelAutocomplete;
